/**
 * @file Powerup entity factory (GDD §Fase 3 — powerups).
 *
 * A dropped powerup is a trigger with a lifetime: it never collides with
 * anything, it just waits to be walked over by
 * {@link ../systems/PickupSystem.js} and expires on its own if nobody does.
 *
 * Weapons are the one exception. They are dropped the same way but carry an
 * `InteractionTarget`, which takes them out of the automatic vacuum and hands
 * them to {@link ../systems/InteractionSystem.js}: swapping guns is a decision,
 * so it costs a key press.
 */

import {
  Transform,
  Collider,
  Renderable,
  Lifetime,
  Pickup,
  InteractionTarget,
  Weapon
} from '../components/index.js';
import { Layer } from '../config/Layers.js';
import { POWERUPS, DROPS, pickupDefinition, weaponPickupKey } from '../config/Powerups.js';
import { Prefab } from '../rendering/Prefabs.js';

/**
 * Drops a powerup on the floor.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {number} x World X.
 * @param {number} z World Z.
 * @param {string} key Key into {@link ../config/Powerups.js POWERUPS}, or a
 *   weapon archetype key.
 * @param {import('../components/Weapon.js').Weapon} [weapon] Existing stat block
 *   to drop, for a gun that was already in use. Built from the archetype when
 *   omitted.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createPickup(ctx, x, z, key, weapon = null) {
  const definition = pickupDefinition(key);
  if (!definition) throw new ReferenceError(`Unknown powerup "${key}".`);

  const { world, meshFactory } = ctx;
  const entity = world.createEntity('pickup');
  const isWeapon = definition.kind === 'weapon';

  // The stat block travels with the drop, so a half-cooled gun stays half-cooled
  // and anything added to `Weapon` later (ammo, heat, mods) follows for free.
  const stats = isWeapon ? weapon ?? Weapon.fromArchetype(definition.weapon) : null;

  entity.add(new Transform(x, DROPS.hoverHeight, z));
  entity.add(new Pickup(key, definition, stats));
  entity.add(new Lifetime(isWeapon ? DROPS.weaponLifetime : DROPS.lifetime));

  // Guns wait for `E` instead of being walked over.
  if (isWeapon) entity.add(new InteractionTarget(definition.name));

  // Trigger only: the collider exists so future systems can query it by layer,
  // but the collision system never resolves it.
  entity.add(
    new Collider({
      halfX: DROPS.pickupRadius,
      halfZ: DROPS.pickupRadius,
      layer: Layer.PICKUP,
      mask: Layer.PLAYER,
      isTrigger: true
    })
  );

  entity.add(
    new Renderable(Prefab.pickup(key), meshFactory.acquire(Prefab.pickup(key)), {
      yOffset: 0
    })
  );

  return entity;
}

/**
 * Puts a weapon the player was carrying back on the floor.
 *
 * It is an ordinary weapon pickup plus two things: the gun's own state travels
 * with it, so ammo and cooldown belong to the instance and not to the type; and
 * it starts disarmed, because it lands inside the pickup radius of the player
 * who dropped it.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {number} x World X.
 * @param {number} z World Z.
 * @param {import('../components/Weapon.js').Weapon} weapon The weapon being dropped.
 * @returns {import('../ecs/Entity.js').Entity | null} The drop, or null when the
 *   weapon has no floor representation.
 */
export function createWeaponDrop(ctx, x, z, weapon) {
  const key = weaponPickupKey(weapon.key);
  if (!key) {
    console.warn(`Weapon "${weapon.key}" has no pickup definition; it cannot be dropped.`);
    return null;
  }

  const entity = createPickup(ctx, x, z, key, weapon);
  const pickup = entity.get(Pickup);
  pickup.weaponState = weapon.captureState();
  pickup.armed = false;

  return entity;
}
